import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Youtube, CheckCircle2, Loader2 } from "lucide-react";
import logo from '../../assets/images/film-artie.png';
import podcastIcon from '../../assets/images/podcast.png';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useOnboarding } from "@/context/OnboardingContext";
import { apiClient } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";
import { toast } from "sonner";

export default function NewProjectAudience() {
    const navigate = useNavigate();
    const { data, updateData } = useOnboarding();
    const { user, checkAuth } = useAuthStore();

    // Section 1: Digital Following State
    const [instagram, setInstagram] = useState("");
    const [tiktok, setTiktok] = useState("");
    const [spotify, setSpotify] = useState("");
    const [applePodcast, setApplePodcast] = useState("");
    const [isEarly, setIsEarly] = useState(false);

    const [isSaving, setIsSaving] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const validateUrl = (url: string) => {
        if (!url) return true;
        try {
            new URL(url);
            return true;
        } catch {
            return false;
        }
    };

    // Pre-fill data from user object if available (e.g. navigating back)
    useEffect(() => {
        if (user?.social_profiles) {
            const insta = user.social_profiles.find(p => p.platform === "instagram")?.handle;
            const tik = user.social_profiles.find(p => p.platform === "tiktok")?.handle;
            const spot = user.social_profiles.find(p => p.platform === "spotify")?.handle;
            const apple = user.social_profiles.find(p => p.platform === "apple_podcasts")?.handle;

            if (insta) setInstagram(insta);
            if (tik) setTiktok(tik);
            if (spot) setSpotify(spot);
            if (apple) setApplePodcast(apple);
        }

        // Also check "isEarly" or similar if we were to persist it on user (currently seemingly local only?)
        // The 'isEarly' state is not clearly mapped to user profile in the save logic above?
        // Ah, save logic: const updatePayload: any = {}; ... updateCurrentUser(updatePayload);
        // It only saves social_profiles. 'isEarly' is lost if not saved to backend.
    }, [user]);

    const handleConnectYouTube = async () => {
        try {
            // Set flag to indicate we are intentionally connecting for this session
            sessionStorage.setItem("project_connecting_youtube", "true");
            localStorage.setItem("oauth_return_path", window.location.pathname);
            const { url } = await apiClient.connectYouTube();
            window.location.href = url;
        } catch (error) {
            console.error(error);
            toast.error("Failed to initiate YouTube connection");
            sessionStorage.removeItem("project_connecting_youtube");
        }
    };

    // Determine if YouTube is connected for THIS flow.
    // We only show it as connected if the user actually has data AND we have flagged that we are connecting/connected in this session.
    // This allows the form to appear "reset" (unconnected) initially, even if the user has a global connection.
    const [isYouTubeConnected, setIsYouTubeConnected] = useState(false);

    useEffect(() => {
        const justConnected = sessionStorage.getItem("project_connecting_youtube") === "true";
        if (user?.youtube_data && justConnected) {
            setIsYouTubeConnected(true);
        } else {
            setIsYouTubeConnected(false);
        }
    }, [user]);

    const handleNext = async () => {
        const newErrors: Record<string, string> = {};
        if (spotify && !validateUrl(spotify)) newErrors.spotify = "Please enter a valid URL";
        if (applePodcast && !validateUrl(applePodcast)) newErrors.applePodcast = "Please enter a valid URL";

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            toast.error("Please fix the validation errors before continuing");
            return;
        }

        try {
            setIsSaving(true);

            // Save data to context
            updateData({
                instagram,
                tiktok,
                spotify,
                applePodcast,
                isEarly,
            });

            // Prepare social profiles for backend
            const socialProfiles: any[] = [];
            if (instagram) socialProfiles.push({ platform: "instagram", handle: instagram });
            if (tiktok) socialProfiles.push({ platform: "tiktok", handle: tiktok });
            if (spotify) socialProfiles.push({ platform: "spotify", handle: spotify });
            if (applePodcast) socialProfiles.push({ platform: "apple_podcasts", handle: applePodcast });

            // Construct update object
            const updatePayload: any = {};

            if (socialProfiles.length > 0) {
                updatePayload.social_profiles = socialProfiles;
            }

            // Only call update if we have something to save
            if (socialProfiles.length > 0) {
                await apiClient.updateCurrentUser(updatePayload);
                await checkAuth(); // Refresh user data
            }

            navigate('/projects/create/team', { replace: true });
        } catch (error) {
            console.error("Error saving data:", error);
            toast.error("Failed to save data. Please try again.");
        } finally {
            setIsSaving(false);
        }
    };

    // Validation Logic
    const hasDigital = isYouTubeConnected || instagram || tiktok || spotify || applePodcast;
    const canProceed = hasDigital || isEarly;

    return (
        <div className="min-h-screen selection:bg-[oklch(0.70_0.18_55)] selection:text-[oklch(1_0_0)] relative bg-white text-[oklch(0.2686_0_0)] overflow-x-hidden flex flex-col items-center font-sans">

            <main className="relative z-10 w-full flex flex-col items-center min-h-screen">

                <div className="w-full max-w-3xl px-6 py-12 flex flex-col items-center flex-grow">
                    {/* Logo */}
                    <div className="mb-8 relative">
                        <div className="absolute inset-0 bg-[oklch(0.97_0.01_60)] blur-xl opacity-50"></div>
                        <img
                            src={logo}
                            alt="Artie"
                            className="w-48 h-48 object-contain relative z-10"
                        />
                    </div>

                    {/* Header Section */}
                    <div className="text-center max-w-2xl mb-10 animate-in slide-in-from-bottom-4 duration-500">
                        <h1 className="text-4xl md:text-5xl font-bold mb-6 font-cabinet tracking-tight text-[oklch(0.2686_0_0)]">
                            Help me understand <br />your audience
                        </h1>
                        <p className="text-lg text-[oklch(0.55_0_0)] font-ibm leading-relaxed mb-2">
                            Everyone has an audience for their work - some have an established following on digital platforms, while others are known through film, TV, or festival circuits. Either way, share what you can - audience reach is the foundation for connecting with brand partners.
                            Don't have much of an audience yet? That's okay. We'll help you grow your reach and identify who will resonate with your project.
                        </p>
                    </div>

                    {/* Main Form Container */}
                    <Card className="w-full bg-white shadow-xl border border-[oklch(0.92_0_0)] overflow-hidden animate-in slide-in-from-bottom-4 duration-500 delay-100 p-8 md:p-10">

                        <div className="space-y-10">

                            {/* Section 1: Digital Following */}
                            <div className="space-y-6">
                                <div className="space-y-1">
                                    <label className="text-xl font-bold text-[oklch(0.2686_0_0)] font-cabinet">
                                        Do you have a following?
                                    </label>
                                    <p className="text-sm text-[oklch(0.55_0_0)] leading-tight">
                                        This helps me estimate your reach and find brands that align with your audience.
                                    </p>
                                </div>

                                {/* Connect YouTube */}
                                <div className="space-y-2">
                                    <Button
                                        className={`w-full h-14 text-lg font-medium rounded-xl shadow-sm transition-all flex items-center justify-start px-4 gap-3 ${isYouTubeConnected
                                            ? "bg-green-50 border border-green-200 text-green-700 hover:bg-green-100"
                                            : "bg-white hover:bg-[oklch(0.98_0_0)] border border-[oklch(0.92_0_0)] text-[oklch(0.2686_0_0)]"
                                            }`}
                                        onClick={isYouTubeConnected ? undefined : handleConnectYouTube}
                                        disabled={isYouTubeConnected}
                                    >
                                        {isYouTubeConnected ? (
                                            <>
                                                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                                                </div>
                                                YouTube Connected
                                            </>
                                        ) : (
                                            <>
                                                <div className="w-8 h-8 bg-[#FF0000] rounded-full flex items-center justify-center">
                                                    <Youtube className="w-4 h-4 text-white" />
                                                </div>
                                                Connect YouTube
                                            </>
                                        )}
                                    </Button>
                                </div>

                                {/* Manual Social Handles */}
                                <div className="space-y-4">
                                    {/* Instagram */}
                                    <div className="relative">
                                        <div className="absolute left-4 top-1/2 -translate-y-1/2">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" fill="#E1306C" />
                                            </svg>
                                        </div>
                                        <Input
                                            placeholder="Add Instagram handle here"
                                            value={instagram}
                                            onChange={(e) => setInstagram(e.target.value)}
                                            className="bg-gray-50 border-[oklch(0.92_0_0)] focus:border-[oklch(0.70_0.18_55)] focus:ring-[oklch(0.70_0.18_55)] h-12 text-base pl-12"
                                        />
                                    </div>

                                    {/* TikTok */}
                                    <div className="relative">
                                        <div className="absolute left-4 top-1/2 -translate-y-1/2">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="text-black" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                                            </svg>
                                        </div>
                                        <Input
                                            placeholder="Add TikTok handle here"
                                            value={tiktok}
                                            onChange={(e) => setTiktok(e.target.value)}
                                            className="bg-gray-50 border-[oklch(0.92_0_0)] focus:border-[oklch(0.70_0.18_55)] focus:ring-[oklch(0.70_0.18_55)] h-12 text-base pl-12"
                                        />
                                    </div>

                                    {/* Spotify */}
                                    <div>
                                        <div className="relative">
                                            <div className="absolute left-4 top-1/2 -translate-y-1/2">
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="#1DB954" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141 4.32-1.38 9.841-.719 13.44 1.56.42.24.6.841.301 1.261zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
                                                </svg>
                                            </div>
                                            <Input
                                                placeholder="Paste Spotify show link here"
                                                value={spotify}
                                                onChange={(e) => {
                                                    setSpotify(e.target.value);
                                                    if (errors.spotify) setErrors(prev => ({ ...prev, spotify: undefined }));
                                                }}
                                                className={`bg-gray-50 border-[oklch(0.92_0_0)] focus:border-[oklch(0.70_0.18_55)] focus:ring-[oklch(0.70_0.18_55)] h-12 text-base pl-12 ${errors.spotify ? "border-red-500 focus:border-red-500 focus:ring-red-500" : ""}`}
                                            />
                                        </div>
                                        {errors.spotify && <p className="text-red-500 text-xs mt-1 ml-1">{errors.spotify}</p>}
                                    </div>

                                    {/* Apple Podcast */}
                                    <div>
                                        <div className="relative">
                                            <div className="absolute left-4 top-1/2 -translate-y-1/2">
                                                <img
                                                    src={podcastIcon}
                                                    alt="Apple Podcast"
                                                    className="w-5 h-5 object-contain"
                                                />
                                            </div>
                                            <Input
                                                placeholder="Paste Apple Podcasts show link here"
                                                value={applePodcast}
                                                onChange={(e) => {
                                                    setApplePodcast(e.target.value);
                                                    if (errors.applePodcast) setErrors(prev => ({ ...prev, applePodcast: undefined }));
                                                }}
                                                className={`bg-gray-50 border-[oklch(0.92_0_0)] focus:border-[oklch(0.70_0.18_55)] focus:ring-[oklch(0.70_0.18_55)] h-12 text-base pl-12 ${errors.applePodcast ? "border-red-500 focus:border-red-500 focus:ring-red-500" : ""}`}
                                            />
                                        </div>
                                        {errors.applePodcast && <p className="text-red-500 text-xs mt-1 ml-1">{errors.applePodcast}</p>}
                                    </div>

                                    {/* Escape Hatch Checkbox */}
                                    <div className="flex items-center space-x-3 p-4 border border-[oklch(0.92_0_0)] rounded-xl bg-gray-50">
                                        <Checkbox
                                            id="early-stage"
                                            checked={isEarly}
                                            onCheckedChange={(checked) => setIsEarly(checked as boolean)}
                                            className="data-[state=checked]:bg-[oklch(0.2686_0_0)] data-[state=checked]:border-[oklch(0.2686_0_0)] data-[state=checked]:text-white"
                                        />
                                        <label
                                            htmlFor="early-stage"
                                            className="text-base font-medium text-[oklch(0.2686_0_0)] cursor-pointer flex-1"
                                        >
                                            I'm early and don't have a following yet
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>

                </div>

                {/* Footer & Navigation */}
                <div className="w-full mt-auto pb-10 px-6 md:px-20">
                    <div className="w-full border-t border-[oklch(0.92_0_0)] mb-8" />
                    <div className="w-full flex items-center justify-between">
                        <div className="flex-1 flex justify-start">
                            <Button
                                variant="ghost"
                                className="text-[oklch(0.55_0_0)] hover:text-[oklch(0.2686_0_0)] hover:bg-transparent pl-0"
                                onClick={() => {
                                    navigate(data.projectId ? `/projects/create/step-2?projectId=${data.projectId}` : '/projects/create/step-2');
                                }}
                            >
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Back
                            </Button>
                        </div>

                        {/* Progress Indicators */}
                        <div className="flex gap-2 justify-center">
                            <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.92_0_0)]"></div>
                            <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.2686_0_0)]"></div>
                            <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.92_0_0)]"></div>
                            <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.92_0_0)]"></div>
                        </div>

                        <div className="flex-1 flex justify-end">
                            <Button
                                className="bg-black hover:bg-black/90 text-white rounded-xl px-8 h-12 shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={handleNext}
                                disabled={isSaving || !canProceed}
                            >
                                {isSaving ? (
                                    <>
                                        Saving... <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                                    </>
                                ) : (
                                    <>
                                        Continue
                                        <ArrowRight className="w-4 h-4 ml-2" />
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </div>

            </main>
        </div>
    );
}